import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Archive, Download, Eye, Info, PackagePlus, Plus } from 'lucide-react';
import clsx from 'clsx';
import * as XLSX from 'xlsx';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { Switch } from '../../../../components/ui/Switch';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { useNotice } from '../../../../hooks/useNotice';
import { SupplyFormModal } from './modals/SupplyFormModal';
import { SUPPLY_MOVEMENT, SupplyMovementModal } from './modals/SupplyMovementModal';
import { fetchSupplies } from '../../../../features/supplies/suppliesThunks';
import { setSupplyFilters } from '../../../../features/supplies/suppliesSlice';
import {
  boxLabel,
  buildSuppliesExportRows,
  formatMoney,
  formatPercent,
  suppliesExportFileName,
  supplyKindLabel,
} from '../../../../features/supplies/suppliesUtils';
import {
  selectCurrentWarehouse,
  selectWarehouses,
} from '../../../../features/warehouseContext/warehouseContextSlice';
import { formatQuantity } from '../../../../utils/inventoryLabels';

const SUPPLIES_PATH = '/warehouse/supplies';

/**
 * `/warehouse/supplies`: what the warehouse uses up to operate — boxes, tape,
 * film.
 *
 * Not merchandise of a client, so it keeps its own book and its own cost, and
 * it belongs to the **warehouse**: two centres have two stocks of boxes at two
 * costs, because the supplier and the freight change. Whoever only stores there
 * neither sees nor handles them — the server answers the supplies of the
 * warehouses this session holds, and nothing else.
 *
 * The listing is for recognising a supply, replenishing it and going in: the
 * adjustment, the prices by client and the history live on its page.
 */
export const Supplies = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { supplies, loading, error, filters } = useSelector((state) => state.supplies);
  const warehouse = useSelector(selectCurrentWarehouse);
  const warehouses = useSelector(selectWarehouses);

  const [searchInput, setSearchInput] = useState(filters.search);
  const [isCreating, setIsCreating] = useState(false);
  const [replenishing, setReplenishing] = useState(null);
  const setNotice = useNotice('supplies-notice');

  // Debounce for search
  useEffect(() => {
    const handler = setTimeout(() => {
      const search = searchInput.trim();
      if (search !== filters.search) dispatch(setSupplyFilters({ search }));
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  // Fetch when the warehouse in use or a filter the server applies changes
  useEffect(() => {
    dispatch(fetchSupplies());
  }, [dispatch, warehouse?.id, filters.search, filters.onlyActive]);


  const canManage = Boolean(warehouse?.can_manage);
  const showWarehouse = !warehouse && warehouses.length > 1;

  const handleExport = () => {
    const sheet = XLSX.utils.json_to_sheet(buildSuppliesExportRows(supplies));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Insumos');
    XLSX.writeFile(book, suppliesExportFileName(warehouse));
  };

  // The server sends every supply that matches; the table pages them here.
  const lastPage = Math.max(1, Math.ceil(supplies.length / filters.rowsPerPage));
  const page = Math.min(filters.page, lastPage);
  const rows = supplies.slice((page - 1) * filters.rowsPerPage, page * filters.rowsPerPage);

  const columns = [
    ...(showWarehouse
      ? [
          {
            header: 'BODEGA',
            cell: (row) => <span className="text-text-secondary">{row.warehouse_name}</span>,
          },
        ]
      : []),
    {
      header: 'INSUMO',
      cell: (row) => (
        <div className="flex flex-col max-w-[300px]">
          <span className="font-bold font-mono text-ninja-dark dark:text-white">{row.sku}</span>
          <span className="text-xs text-text-muted mt-0.5 truncate" title={row.name}>
            {row.name}
          </span>
        </div>
      ),
    },
    {
      // What kind of thing it is; a box also says what it lends to a parcel.
      header: 'TIPO',
      cell: (row) => (
        <div className="flex flex-col">
          <span data-testid="supply-kind-cell">{supplyKindLabel(row.kind)}</span>
          {boxLabel(row) && (
            <span className="text-xs text-text-muted mt-0.5 tabular-nums">{boxLabel(row)}</span>
          )}
        </div>
      ),
    },
    {
      // What it costs and what is left on top of it, which is one thought.
      header: 'COSTO PROMEDIO',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="tabular-nums">
            {Number(row.average_cost) ? formatMoney(row.average_cost) : 'Sin costo'}
          </span>
          <span
            className={clsx(
              'text-xs mt-0.5',
              row.margin !== null && row.margin < 0 ? 'text-danger font-medium' : 'text-text-muted'
            )}
          >
            Margen {formatPercent(row.margin)}
          </span>
        </div>
      ),
    },
    {
      header: 'PRECIO',
      cell: (row) => {
        const exceptions = row.client_prices?.length || 0;
        return (
          <div className="flex flex-col">
            <span className="tabular-nums">
              {row.default_price === null ? 'No se cobra' : formatMoney(row.default_price)}
            </span>
            {exceptions > 0 && (
              <span className="text-xs text-text-muted mt-0.5">
                +{exceptions} precio{exceptions === 1 ? '' : 's'} por cliente
              </span>
            )}
          </div>
        );
      },
    },
    {
      header: 'SALDO',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-semibold tabular-nums">{formatQuantity(row.quantity)}</span>
          <span className="text-xs text-text-muted mt-0.5">
            Valor {formatMoney(row.stock_value)}
          </span>
        </div>
      ),
    },
    {
      header: 'ESTADO',
      cell: (row) => (
        <ToneBadge tone={row.is_active ? 'success' : 'neutral'} testId="supply-status">
          {row.is_active ? 'Activo' : 'De baja'}
        </ToneBadge>
      ),
    },
    {
      header: 'ACCIONES',
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="subtle"
            size="sm"
            iconOnly
            icon={PackagePlus}
            title="Reponer: registrar una compra que ingresa"
            aria-label={`Reponer ${row.sku}`}
            data-testid="supply-replenish"
            className="hover:text-ninja-green"
            onClick={() => setReplenishing(row)}
          />
          <Button
            variant="subtle"
            size="sm"
            iconOnly
            icon={Eye}
            title="Ver detalle, historial y precios por cliente"
            aria-label={`Ver ${row.sku}`}
            data-testid="supply-view"
            onClick={() => navigate(`${SUPPLIES_PATH}/${row.id}`)}
          />
        </div>
      ),
    },
  ];

  // An empty table has to say why: empty looks the same as broken.
  const emptyMessage = (() => {
    if (filters.search !== '') return 'No hay insumos que coincidan con estos filtros.';
    if (!warehouse) return 'Las bodegas que administras todavía no tienen insumos.';
    return canManage
      ? `${warehouse.name} todavía no tiene insumos. Crea el primero y repón su stock para que empiece a costear.`
      : `No administras los insumos de ${warehouse.name}.`;
  })();

  return (
    <div data-testid="supplies-page" className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={Archive}
        title="Insumos"
        description="Lo que la bodega consume para operar: cajas, cinta, film. No es mercadería de un cliente, así que lleva su propio libro y su propio costo."
        actions={
          // Drawn for whoever could use it somewhere, and disabled — saying
          // why — until a warehouse is picked. Not drawn in one held by
          // somebody else.
          (!warehouse || canManage) && (
            <Button
              variant="primary"
              icon={Plus}
              disabled={!canManage}
              title={canManage ? undefined : 'Elige arriba la bodega donde quieres crear el insumo'}
              onClick={() => setIsCreating(true)}
              data-testid="supply-add"
            >
              Nuevo insumo
            </Button>
          )
        }
      />

      {warehouse && !canManage && (
        <p
          data-testid="supplies-readonly-note"
          className="flex items-center gap-2 text-sm text-text-secondary"
        >
          <Info size={16} className="text-info flex-shrink-0" />
          Guardas mercadería en {warehouse.name}, pero los insumos son de quien administra el
          espacio: no los ves ni los manejas.
        </p>
      )}

      <FilterBar
        variant="bare"
        testId="supplies-filters"
        actions={
          <Button
            variant="ghost"
            icon={Download}
            onClick={handleExport}
            disabled={supplies.length === 0}
            title={
              supplies.length > 0
                ? 'Excel de los insumos que coinciden con los filtros'
                : 'No hay insumos que exportar'
            }
            data-testid="supplies-export"
          >
            Exportar
          </Button>
        }
      >
        <SearchInput
          variant="bare"
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Buscar por SKU, nombre o EAN..."
          testId="supplies-search"
        />
        <Switch
          checked={filters.onlyActive}
          onChange={(onlyActive) => dispatch(setSupplyFilters({ onlyActive }))}
          label="Solo activos"
          testId="supplies-only-active"
        />
      </FilterBar>

      <DataTable
        columns={columns}
        data={rows}
        totalItems={supplies.length}
        currentPage={page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={loading}
        error={error}
        onRetry={() => dispatch(fetchSupplies())}
        itemLabel="insumos"
        emptyMessage={emptyMessage}
        onPageChange={(nextPage) => dispatch(setSupplyFilters({ page: nextPage }))}
        onRowsPerPageChange={(rowsPerPage) => dispatch(setSupplyFilters({ rowsPerPage }))}
      />

      <SupplyFormModal
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
        warehouse={warehouse}
        supply={null}
        onSaved={(message) => setNotice({ type: 'success', message })}
      />

      <SupplyMovementModal
        mode={replenishing ? SUPPLY_MOVEMENT.REPLENISH : null}
        supply={replenishing}
        onClose={() => setReplenishing(null)}
        onDone={(message) => setNotice({ type: 'success', message })}
      />

    </div>
  );
};
